/**
 * `useRawReveal` — blur-safe hold-to-reveal state machine for the
 * VM Debug pane's raw-payload reveal gate (cloud-VM supportability
 * TDD §B-1 "Hold-to-reveal").
 *
 * # Why two conditions
 *
 * Raw reveal is gated on BOTH a deliberate "arm" click AND an Alt
 * key currently held. Either alone is insufficient:
 *
 *   - Arm alone would expose the raw payloads any time the user
 *     opened the pane and forgot it.
 *   - Alt alone would expose them every time the engineer
 *     keyboard-navigated the OS (Alt+Tab on Windows / Cmd+Tab
 *     elsewhere; Alt is a common modifier).
 *
 * Both together creates a deliberate, transient affordance: the
 * engineer clicks "Show raw", then holds Alt over the line they
 * want to inspect. Releasing Alt OR un-arming hides the payload.
 *
 * # Auto-disarm — five paths (TDD §B-1)
 *
 * Any of these resets `toggleArmed` to `false`:
 *   1. Window blur — Alt+Tab clears arm so a re-focus doesn't
 *      auto-reveal.
 *   2. `visibilitychange` → hidden — same intent; covers macOS
 *      Mission Control + iOS Safari background.
 *   3. Escape keydown.
 *   4. 30s timeout since arm — auto-disarm regardless of Alt
 *      state (the timer continues while raw is actively visible —
 *      Codex round-2 MINOR pin).
 *   5. The user clicks "Show raw" again (toggle off).
 *
 * # Lost-keyup safety
 *
 * `keyup` can be missed if the Alt release happens while the window
 * is unfocused. The `blur` auto-disarm catches this: without arm,
 * raw is impossible regardless of Alt state. Even if the renderer's
 * internal `altKeyDown` stays stuck true, `rawActive = arm && alt`
 * becomes false the instant blur lands.
 *
 * # 7 test cases (TDD §B-1)
 *
 *   1. Arm + Alt-hold → rawActive true
 *   2. Arm + Alt-hold + blur → rawActive false, toggleArmed false
 *   3. Arm + Alt-hold + Escape → rawActive false, toggleArmed false
 *   4. Arm + 30s wait → toggleArmed false, no Alt-press required
 *   5. Arm + Alt-hold + 30s elapsed → toggleArmed false even though
 *      raw is actively visible (timer continues while raw is on)
 *   6. Arm + Alt-down + visibility hidden → both reset
 *   7. Arm with no Alt-press → rawActive false
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Default auto-disarm timeout — 30s per TDD §B-1 test case 4 + 5. */
export const DEFAULT_AUTO_DISARM_MS = 30_000;

/**
 * Test-only injectable seam. Production calls `useRawReveal()` with
 * no args; tests inject a smaller timeout + a synthetic `window`-
 * like target so they can drive `keydown` / `blur` /
 * `visibilitychange` without a real DOM.
 *
 * The deps bag is deliberately small — the hook's external surface
 * is the state, not the wiring.
 */
export interface UseRawRevealDeps {
  /** Override the 30s auto-disarm. Tests pass a smaller value. */
  autoDisarmMs?: number;
}

export interface UseRawRevealState {
  /** True iff `toggleArmed` AND `altKeyDown`. The pane uses this to
   *  decide whether to render the `<RawRevealOverlay>` watermark
   *  + reveal the raw payloads. */
  rawActive: boolean;
  /** True after the engineer clicked "Show raw" (or `armToggle` was
   *  called) and the auto-disarm timer hasn't elapsed. */
  toggleArmed: boolean;
  /** Imperative arm — flip `toggleArmed` to true + reset the 30s
   *  timer. Idempotent. */
  armToggle: () => void;
  /** Imperative disarm — flip `toggleArmed` to false + clear timer.
   *  Idempotent. */
  disarmToggle: () => void;
}

export function useRawReveal(deps: UseRawRevealDeps = {}): UseRawRevealState {
  const autoDisarmMs = deps.autoDisarmMs ?? DEFAULT_AUTO_DISARM_MS;
  const [toggleArmed, setToggleArmed] = useState(false);
  const [altKeyDown, setAltKeyDown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable disarm callback — used by the timer + the global event
  // listeners. Wrapped in useCallback so the effect dependency below
  // doesn't churn on every render.
  const disarmToggle = useCallback(() => {
    setToggleArmed(false);
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Arm: flip the state + (re)start the 30s timer. Idempotent in the
  // sense that re-arming while armed just resets the timer — the user
  // gets the full 30s from the most recent arm. Test case 4 + 5 pin
  // this.
  const armToggle = useCallback(() => {
    setToggleArmed(true);
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      // Disarm directly — calling `disarmToggle` would also clear
      // `timerRef.current`, but the timer has already fired so the
      // ref is effectively self-cleaned by the setTimeout callback
      // semantics. Belt-and-braces: zero it.
      setToggleArmed(false);
      timerRef.current = null;
    }, autoDisarmMs);
  }, [autoDisarmMs]);

  useEffect(() => {
    // SSR / test environments without a real DOM may bail here. The
    // happy-dom test environment provides `window` and the event
    // listeners; jsdom + production both do too.
    if (typeof window === 'undefined') return;

    const onKeyDown = (event: KeyboardEvent): void => {
      // Track Alt explicitly. `event.altKey` is set on every keydown
      // (not just the Alt key itself), so we filter on `event.key`.
      // This means `altKeyDown` only flips on a fresh Alt press; a
      // hold-Alt-then-press-A keypress doesn't re-fire.
      if (event.key === 'Alt') {
        setAltKeyDown(true);
      }
      if (event.key === 'Escape') {
        // Bugbot round-4 LOW fix: reset altKeyDown too, mirroring the
        // blur + visibility-hidden handlers. Without this, holding Alt
        // + pressing Escape + re-arming would auto-reveal because
        // `altKeyDown` would still be `true` from the original Alt
        // press — bypassing the two-condition safety gate the hook
        // enforces.
        setAltKeyDown(false);
        disarmToggle();
      }
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Alt') {
        setAltKeyDown(false);
      }
    };

    const onBlur = (): void => {
      // Reset BOTH — alt may be stuck true if the keyup happened off-
      // window (lost-keyup case). Without resetting altKeyDown the
      // next re-focus + arm would auto-reveal on a stale Alt state.
      setAltKeyDown(false);
      disarmToggle();
    };

    const onVisibilityChange = (): void => {
      if (typeof document !== 'undefined' && document.hidden) {
        // Same as onBlur — both gates collapse.
        setAltKeyDown(false);
        disarmToggle();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      // Defensive: clear the timer on unmount so a stale fire can't
      // mutate state after the component is gone.
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [disarmToggle]);

  return {
    rawActive: toggleArmed && altKeyDown,
    toggleArmed,
    armToggle,
    disarmToggle,
  };
}
